import React, { useEffect, useContext } from 'react'
import classNames from 'classnames'
import MainNavigationContext from '@toasttab/shared/src/data/HeaderNavigation/context/MainNavigationContext'
import { SubMenus as MobileSubMenus } from '@toasttab/shared/src/components/organisms/Navigation/Mobile/SubMenus'
import SectionCTA from '@toasttab/shared/src/components/organisms/Navigation/Mobile/ctas/SectionCTA'
import DemoCTA from '@toasttab/shared/src/components/organisms/Navigation/Mobile/ctas/DemoCTA'
import {
  ParentMenuProps,
  ParentMenusProps
} from '@toasttab/shared/src/data/HeaderNavigation/MainNavigationMenu.d'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import { calcSubMenuHeight } from '@toasttab/shared/src/components/organisms/Navigation/Mobile/SubMenus'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'

export const calcParentMenuHeight = (windowSize: any) => {
  const headerEl = document?.getElementsByTagName('header')
  const parentMobileEL = document?.getElementById('mobile-parent-nav')
  if (windowSize.width < 992 && parentMobileEL) {
    parentMobileEL.style.height = `calc(100vh - ${headerEl[0].offsetHeight}px`
  }
}

export const ParentMenus = ({ navigationData }: ParentMenusProps) => {
  const {
    selectedParentMenu,
    mobileNavOpen,
    setSelectedParentMenu,
    setMobileNavOpen
  } = useContext(MainNavigationContext)
  const localizationContext = useContext(I18nextContext)

  const windowSize = useWindowSize()

  useEffect(() => {
    window.addEventListener('resize', calcParentMenuHeight)
    calcParentMenuHeight(windowSize)
  }, [windowSize.width])

  useEffect(() => {
    const bodyEl = document.getElementsByTagName('body')

    if (mobileNavOpen) {
      bodyEl[0].style.overflow = 'hidden'
    } else {
      bodyEl[0].style.removeProperty('overflow')
    }
  })

  const mobileNavigationCss = classNames(
    'fixed z-[90] md:z-[95] bg-white transition-right md:transition-left !duration-500 md:border-r md:border-gray-25 w-full md:max-w-[375px] w-screen',
    {
      'right-[-800px] md:left-[-500px]':
        !mobileNavOpen || windowSize.width >= 992,
      'right-0 md:left-0 pb-40': mobileNavOpen && windowSize.width < 992
    }
  )

  const overlayCss = classNames({
    overlay: true,
    hidden: !mobileNavOpen || windowSize.width > 992,
    'fixed bg-black opacity-30 w-full h-full z-[80]':
      mobileNavOpen && windowSize.width < 992
  })

  const handleClick = (label: string) => {
    calcSubMenuHeight(windowSize)
    setSelectedParentMenu(label != selectedParentMenu ? label : null)
  }

  const toggleMobileMenu = () => {
    setSelectedParentMenu(null)
    setMobileNavOpen(false)
  }

  const generateParentMenu = (menu: ParentMenuProps) => {
    const isExpanded = menu.label == selectedParentMenu
    const subMenuCss = classNames(
      'hover:bg-gray-25 focus:bg-gray-25 p-4 border-b border-b-gray-25 border-r-4 border-r-transparent hover:border-r-blue-75 focus:border-r-blue-75',
      {
        'bg-gray-25 border-r-blue-75': isExpanded
      }
    )
    const openButtonCss = classNames(
      'relative flex items-center justify-between text-left w-full after:p-3 h-[80px]',
      menu.heapClass,
      {
        'after:content-icon-chevron-right': !isExpanded
      }
    )
    if (menu.subMenus) {
      return (
        <div
          className={subMenuCss}
          role='none'
          key={`mobile-parent-dropdown-${menu.label}}`}
        >
          <button
            className={openButtonCss}
            role='menuitem'
            aria-haspopup='true'
            aria-expanded={isExpanded}
            onClick={() => handleClick(menu.label)}
            aria-controls={`${menu.label.replace(/\s/g, '-')}-mobile-menu`}
          >
            <div>
              <h4 className='font-effra text-default text-[22px] leading-8 '>
                {menu.label}
              </h4>
              <p className='font-sansPro text-secondary text-xs'>
                {menu.description}
              </p>
            </div>
          </button>
        </div>
      )
    } else {
      const menuHref = localizeLink(
        menu.url,
        localizationContext.language,
        localizationContext
      )
      return (
        <div
          className='hover:bg-gray-25 focus:bg-gray-25 p-4 border-b border-b-gray-25 border-r-4 border-r-transparent hover:border-r-blue-75 focus:border-r-blue-75'
          role='none'
          key={`mobile-parent-link-${menu.label}}`}
        >
          <a
            className={`${menu.heapClass} relative flex items-center justify-between text-left w-full h-[64px]`}
            role='menuitem'
            href={menuHref}
          >
            <div>
              <h4 className='font-effra text-default text-[22px] leading-8 py-4'>
                {menu.label}
              </h4>
              <p className='font-sansPro text-secondary text-xs'>
                {menu.description}
              </p>
            </div>
          </a>
        </div>
      )
    }
  }

  return (
    <div id='mobile-nav-menu' className={mobileNavigationCss}>
      <nav
        id='mobile-parent-nav'
        className='relative z-[100] bg-white pb-40 overflow-y-scroll !no-scrollbar'
        aria-label='Toast Mobile Navigation'
        aria-hidden={!mobileNavOpen}
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className='block'>
          {navigationData.parentMenus.map((menu: any) =>
            generateParentMenu(menu)
          )}
        </div>
        {navigationData.sectionCTA && (
          <SectionCTA {...navigationData.sectionCTA} />
        )}
        <DemoCTA
          demoUrl={localizeLink(
            navigationData.demoUrl,
            localizationContext.language,
            localizationContext
          )}
          loginUrl={navigationData.loginUrl}
          restaurantUrl={navigationData.restaurantUrl}
          showTablet
          siteRegion={navigationData.siteRegion}
        />
      </nav>
      <MobileSubMenus navigationData={navigationData} />
      <div className={overlayCss} onClick={toggleMobileMenu} />
    </div>
  )
}
